// src/lib/services/orchestration/UnifiedDataUpdateOrchestrator.svelte.ts
// Unified data update orchestrator for all real-time updates

import { SvelteMap } from 'svelte/reactivity';
import { eventBus } from '$lib/core/services/EventBusService';
import type {
	DataType,
	UpdateOperationType,
	UpdateOperation,
	UpdateResult,
	UpdateHandler,
	UpdateValidator,
	OrchestratorMetrics,
	OrchestratorConfig,
	UpdateContext,
	UpdateCompleteEvent,
	QueueSizeChangeEvent
} from './types';

/**
 * UnifiedDataUpdateOrchestrator
 *
 * Centralized orchestrator for all data updates in the application.
 * Handles alerts, approved alerts, work orders, and other data points.
 *
 * Features:
 * - Per-data-type locking to prevent race conditions
 * - Queue system for ordered update processing
 * - Support for 5 operation types: add, update, remove, replace, merge
 * - Handler registration per data type
 * - Metrics tracking and monitoring
 * - Validation hooks
 * - Automatic retry on failure
 * - EventBus integration
 *
 * Usage:
 * ```typescript
 * const orchestrator = new UnifiedDataUpdateOrchestrator();
 * orchestrator.registerHandler('alerts', alertsHandler);
 * await orchestrator.processUpdate('alerts', 'update', alertData, context);
 * ```
 */
export class UnifiedDataUpdateOrchestrator {
	// ===========================
	// Reactive State (Svelte 5 Runes)
	// ===========================

	/** Active operations being processed */
	private state = $state({
		activeOperations: new SvelteMap<string, UpdateOperation>(),
		lastUpdateTimestamps: new SvelteMap<DataType, number>(),
		sequenceNumbers: new SvelteMap<DataType, number>(),
		pendingQueue: [] as UpdateOperation[],
		processingLocks: new SvelteMap<DataType, boolean>(),
		metrics: {
			totalUpdates: 0,
			successfulUpdates: 0,
			failedUpdates: 0,
			averageProcessingTime: 0,
			lastProcessingTime: 0,
			updatesByDataType: new SvelteMap<DataType, number>(),
			updatesByOperation: new SvelteMap<UpdateOperationType, number>(),
			queueSizeHistory: []
		} as OrchestratorMetrics
	});

	/** Operations received before a handler was registered — replayed on registerHandler */
	private earlyOperations = new Map<DataType, UpdateOperation[]>();

	/** Registered handlers per data type */
	private handlers = new SvelteMap<DataType, UpdateHandler>();

	/** Registered validators per data type */
	private validators = new SvelteMap<DataType, UpdateValidator>();

	/** Configuration */
	private config: Required<OrchestratorConfig>;

	/** Operation ID counter */
	private operationIdCounter = 0;

	// ===========================
	// Constructor & Configuration
	// ===========================

	constructor(config: OrchestratorConfig = {}) {
		this.config = {
			maxQueueSize: config.maxQueueSize ?? 1000,
			maxRetries: config.maxRetries ?? 3,
			retryDelay: config.retryDelay ?? 1000,
			enableMetrics: config.enableMetrics ?? true,
			enableDebugLogging: config.enableDebugLogging ?? false,
			processingTimeout: config.processingTimeout ?? 30000
		};

		this.log('Orchestrator initialized', this.config);
	}

	// ===========================
	// Handler Registration
	// ===========================

	/**
	 * Register a handler for a specific data type.
	 * If operations arrived before the handler was registered, they are replayed now.
	 */
	registerHandler(dataType: DataType, handler: UpdateHandler): void {
		this.handlers.set(dataType, handler);
		this.log(`Handler registered for ${dataType}`);

		// Replay any operations that arrived before this handler was registered
		const pending = this.earlyOperations.get(dataType);
		if (pending && pending.length > 0) {
			this.log(`Replaying ${pending.length} early operations for ${dataType}`);
			this.earlyOperations.delete(dataType);

			// Re-queue them so they go through the normal processing pipeline
			for (const op of pending) {
				op.status = 'queued';
				this.state.pendingQueue.push(op);
			}

			// Trigger processing (fire-and-forget — errors handled inside processQueue)
			this.processQueue(dataType).catch((err) => {
				this.log(`Error replaying early operations for ${dataType}`, err);
			});
		}
	}

	/**
	 * Register a validator for a specific data type
	 */
	registerValidator(dataType: DataType, validator: UpdateValidator): void {
		this.validators.set(dataType, validator);
		this.log(`Validator registered for ${dataType}`);
	}

	/**
	 * Unregister a handler
	 */
	unregisterHandler(dataType: DataType): void {
		this.handlers.delete(dataType);
		this.validators.delete(dataType);
		this.log(`Handler unregistered for ${dataType}`);
	}

	// ===========================
	// Core Update Processing
	// ===========================

	/**
	 * Process an update operation
	 *
	 * @param dataType - Type of data being updated
	 * @param operation - Type of operation (add, update, remove, replace, merge)
	 * @param data - Data payload
	 * @param context - Update context (source, userId, etc.)
	 * @returns Result of the operation
	 */
	async processUpdate(
		dataType: DataType,
		operation: UpdateOperationType,
		data: unknown,
		context?: Partial<UpdateContext>
	): Promise<UpdateResult> {
		const startTime = performance.now();

		// Create operation
		const updateOperation: UpdateOperation = {
			id: this.generateOperationId(),
			dataType,
			operation,
			data,
			context: {
				source: context?.source ?? 'unknown',
				userId: context?.userId,
				originalMessage: context?.originalMessage,
				metadata: context?.metadata,
				isOptimistic: context?.isOptimistic ?? false,
				priority: context?.priority ?? 0
			},
			timestamp: Date.now(),
			status: 'pending',
			retryCount: 0
		};

		this.log(`Processing update: ${updateOperation.id}`, {
			dataType,
			operation,
			source: updateOperation.context.source
		});

		// Check queue size
		if (this.state.pendingQueue.length >= this.config.maxQueueSize) {
			const error = new Error(`Queue full for ${dataType} (max: ${this.config.maxQueueSize})`);
			this.log(`Queue overflow: ${updateOperation.id}`, error);

			return {
				success: false,
				operationId: updateOperation.id,
				dataType,
				operation,
				error,
				processingTime: performance.now() - startTime
			};
		}

		// Add to queue
		this.state.pendingQueue.push(updateOperation);
		updateOperation.status = 'queued';

		// Emit queue size change event
		this.emitQueueSizeChange(dataType);

		// Process queue
		await this.processQueue(dataType);

		// Get result
		const result = this.getOperationResult(updateOperation.id, startTime);

		// Update metrics
		if (this.config.enableMetrics) {
			this.updateMetrics(result);
		}

		// Emit completion event
		this.emitUpdateComplete(result);

		return result;
	}

	/**
	 * Process queued operations for a specific data type
	 *
	 * NOTE: Uses a loop pattern instead of recursion to prevent stack overflow
	 * when many operations arrive quickly (e.g., multiple alert approvals).
	 */
	private async processQueue(dataType: DataType): Promise<void> {
		// Check if already processing
		if (this.state.processingLocks.get(dataType)) {
			this.log(`Already processing queue for ${dataType}, skipping`);
			return;
		}

		// Acquire lock
		this.state.processingLocks.set(dataType, true);

		try {
			// Keep processing while there are pending operations
			// This loop pattern ensures operations added during processing are handled
			let hasMoreOperations = true;
			while (hasMoreOperations) {
				// Get current batch of operations for this data type
				const operations = this.state.pendingQueue.filter(
					(op) => op.dataType === dataType &&
					        op.status !== 'completed' &&
					        op.status !== 'failed'
				);

				if (operations.length === 0) {
					hasMoreOperations = false;
					break;
				}

				this.log(`Processing batch of ${operations.length} operations for ${dataType}`);

				for (const operation of operations) {
					await this.executeOperation(operation);

					// Remove from queue
					const index = this.state.pendingQueue.indexOf(operation);
					if (index !== -1) {
						this.state.pendingQueue.splice(index, 1);
					}
				}

				// Check if more operations were added while we were processing
				// The loop will continue if there are new pending operations
			}
		} finally {
			// Release lock
			this.state.processingLocks.set(dataType, false);
		}
	}

	/**
	 * Execute a single operation
	 */
	private async executeOperation(operation: UpdateOperation): Promise<void> {
		const { dataType } = operation;
		// 🔍 TIMING: Track orchestrator execution stages
		const timings: Record<string, number> = {};
		const startTime = performance.now();

		// Get handler
		const handler = this.handlers.get(dataType);
		if (!handler) {
			// Buffer the operation — it will be replayed when a handler registers
			if (!this.earlyOperations.has(dataType)) {
				this.earlyOperations.set(dataType, []);
			}
			this.earlyOperations.get(dataType)!.push(operation);
			operation.status = 'pending';
			this.log(`No handler for ${dataType} yet, buffered (${this.earlyOperations.get(dataType)!.length} pending)`);
			return;
		}
		timings.getHandler = performance.now() - startTime;

		// Validate
		const validateStart = performance.now();
		const isValid = await this.validateOperation(operation, handler);
		timings.validate = performance.now() - validateStart;
		if (!isValid) {
			operation.status = 'failed';
			operation.error = new Error(`Validation failed for ${dataType}`);
			this.log(`Validation failed: ${operation.id}`, operation.error);
			return;
		}

		// Mark as processing
		operation.status = 'processing';
		this.state.activeOperations.set(operation.id, operation);
		timings.markProcessing = performance.now() - startTime - timings.getHandler - timings.validate;

		this.log(`⏱️ [ORCH 1/3] Pre-handler setup`, {
			operation: operation.operation,
			getHandler: `${timings.getHandler.toFixed(0)}ms`,
			validate: `${timings.validate.toFixed(0)}ms`,
			markProcessing: `${timings.markProcessing.toFixed(0)}ms`
		});

		try {
			// Process with timeout
			const handlerStart = performance.now();
			const result = await this.processWithTimeout(handler.process(operation));
			timings.handler = performance.now() - handlerStart;

			this.log(`⏱️ [ORCH 2/3] Handler completed`, {
				operation: operation.operation,
				handlerMs: `${timings.handler.toFixed(0)}ms`,
				success: result.success
			});

			if (result.success) {
				operation.status = 'completed';
				this.log(`Operation completed: ${operation.id}`, result);

				// Update timestamps
				const updateStart = performance.now();
				this.state.lastUpdateTimestamps.set(dataType, Date.now());
				const seq = (this.state.sequenceNumbers.get(dataType) ?? 0) + 1;
				this.state.sequenceNumbers.set(dataType, seq);
				timings.updateTimestamps = performance.now() - updateStart;

				timings.total = performance.now() - startTime;
				this.log(`⏱️ [ORCH 3/3] 📊 ORCHESTRATOR TIMING`, {
					operation: operation.operation,
					getHandler: `${timings.getHandler.toFixed(0)}ms`,
					validate: `${timings.validate.toFixed(0)}ms`,
					handler: `${timings.handler.toFixed(0)}ms`,
					updateTimestamps: `${timings.updateTimestamps.toFixed(0)}ms`,
					total: `${timings.total.toFixed(0)}ms`
				});
			} else {
				throw result.error ?? new Error('Operation failed');
			}
		} catch (error) {
			operation.error = error as Error;
			this.log(`Operation failed: ${operation.id}`, error);

			// Retry logic
			if ((operation.retryCount ?? 0) < this.config.maxRetries) {
				operation.retryCount = (operation.retryCount ?? 0) + 1;
				operation.status = 'queued';

				this.log(`Retrying operation: ${operation.id} (attempt ${operation.retryCount})`);

				// Re-queue with delay
				await this.delay(this.config.retryDelay);
				this.state.pendingQueue.push(operation);
			} else {
				operation.status = 'failed';

				// Attempt rollback
				if (handler.rollback) {
					try {
						await handler.rollback(operation);
						this.log(`Rollback successful: ${operation.id}`);
					} catch (rollbackError) {
						this.log(`Rollback failed: ${operation.id}`, rollbackError);
					}
				}
			}
		} finally {
			this.state.activeOperations.delete(operation.id);
		}
	}

	/**
	 * Validate an operation
	 */
	private async validateOperation(
		operation: UpdateOperation,
		handler: UpdateHandler
	): Promise<boolean> {
		// Custom validator
		const validator = this.validators.get(operation.dataType);
		if (validator) {
			const isValid = await validator(operation);
			if (!isValid) return false;
		}

		// Handler validation
		if (handler.validate) {
			return await handler.validate(operation);
		}

		return true;
	}

	/**
	 * Process operation with timeout
	 */
	private async processWithTimeout<T>(promise: Promise<T>): Promise<T> {
		return Promise.race([
			promise,
			new Promise<T>((_, reject) =>
				setTimeout(() => reject(new Error('Operation timeout')), this.config.processingTimeout)
			)
		]);
	}

	// ===========================
	// Metrics & Monitoring
	// ===========================

	/**
	 * Update metrics after an operation
	 */
	private updateMetrics(result: UpdateResult): void {
		this.state.metrics.totalUpdates++;

		if (result.success) {
			this.state.metrics.successfulUpdates++;
		} else {
			this.state.metrics.failedUpdates++;
		}

		// Update average processing time
		const total = this.state.metrics.totalUpdates;
		const current = this.state.metrics.averageProcessingTime;
		this.state.metrics.averageProcessingTime =
			(current * (total - 1) + result.processingTime) / total;

		this.state.metrics.lastProcessingTime = result.processingTime;

		// Update by data type
		const dataTypeCount = this.state.metrics.updatesByDataType.get(result.dataType) ?? 0;
		this.state.metrics.updatesByDataType.set(result.dataType, dataTypeCount + 1);

		// Update by operation
		const operationCount = this.state.metrics.updatesByOperation.get(result.operation) ?? 0;
		this.state.metrics.updatesByOperation.set(result.operation, operationCount + 1);

		// Update queue history (keep last 100)
		this.state.metrics.queueSizeHistory.push(this.state.pendingQueue.length);
		if (this.state.metrics.queueSizeHistory.length > 100) {
			this.state.metrics.queueSizeHistory.shift();
		}
	}

	/**
	 * Get current metrics
	 */
	getMetrics(): OrchestratorMetrics {
		return { ...this.state.metrics };
	}

	/**
	 * Reset metrics
	 */
	resetMetrics(): void {
		this.state.metrics = {
			totalUpdates: 0,
			successfulUpdates: 0,
			failedUpdates: 0,
			averageProcessingTime: 0,
			lastProcessingTime: 0,
			updatesByDataType: new SvelteMap(),
			updatesByOperation: new SvelteMap(),
			queueSizeHistory: []
		};
	}

	// ===========================
	// Event Emission
	// ===========================

	/**
	 * Emit update complete event
	 */
	private emitUpdateComplete(result: UpdateResult): void {
		const event: UpdateCompleteEvent = {
			operationId: result.operationId,
			dataType: result.dataType,
			operation: result.operation,
			success: result.success,
			affectedCount: result.affectedCount,
			error: result.error,
			processingTime: result.processingTime
		};

		eventBus.emit('orchestrator:update-complete', event);
		eventBus.emit(`orchestrator:${result.dataType}:update-complete`, event);

		if (result.success) {
			eventBus.emit(`orchestrator:${result.dataType}:${result.operation}:success`, event);
		} else {
			eventBus.emit(`orchestrator:${result.dataType}:${result.operation}:error`, event);
		}
	}

	/**
	 * Emit queue size change event
	 */
	private emitQueueSizeChange(dataType: DataType): void {
		const event: QueueSizeChangeEvent = {
			size: this.state.pendingQueue.length,
			dataType,
			isFull: this.state.pendingQueue.length >= this.config.maxQueueSize
		};

		eventBus.emit('orchestrator:queue-size-change', event);
		eventBus.emit(`orchestrator:${dataType}:queue-size-change`, event);
	}

	// ===========================
	// Utility Methods
	// ===========================

	/**
	 * Generate unique operation ID
	 */
	private generateOperationId(): string {
		return `op_${Date.now()}_${++this.operationIdCounter}`;
	}

	/**
	 * Get operation result
	 */
	private getOperationResult(operationId: string, startTime: number): UpdateResult {
		const operation = Array.from(this.state.activeOperations.values()).find(
			(op) => op.id === operationId
		);

		if (!operation) {
			// Check in pending queue
			const queuedOp = this.state.pendingQueue.find((op) => op.id === operationId);
			if (queuedOp) {
				return {
					success: queuedOp.status === 'completed',
					operationId: queuedOp.id,
					dataType: queuedOp.dataType,
					operation: queuedOp.operation,
					error: queuedOp.error,
					processingTime: performance.now() - startTime
				};
			}

			return {
				success: false,
				operationId,
				dataType: 'alerts', // Default
				operation: 'update',
				error: new Error('Operation not found'),
				processingTime: performance.now() - startTime
			};
		}

		return {
			success: operation.status === 'completed',
			operationId: operation.id,
			dataType: operation.dataType,
			operation: operation.operation,
			error: operation.error,
			processingTime: performance.now() - startTime
		};
	}

	/**
	 * Delay helper
	 */
	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	/**
	 * Logging helper
	 */
	private log(message: string, data?: any): void {
		// Always log errors/warnings (validation failures, handler errors, queue issues)
		if (message.includes('fail') || message.includes('error') || message.includes('overflow') || message.includes('No handler')) {
			console.warn(`[orchestrator] ${message}`, data || '');
		} else if (message.includes('⏱️')) {
			console.info(`[orchestrator] ${message}`, data || '');
		}
	}

	// ===========================
	// Public Getters
	// ===========================

	/**
	 * Get queue size for a specific data type
	 */
	getQueueSize(dataType?: DataType): number {
		if (dataType) {
			return this.state.pendingQueue.filter((op) => op.dataType === dataType).length;
		}
		return this.state.pendingQueue.length;
	}

	/**
	 * Check if processing is locked for a data type
	 */
	isLocked(dataType: DataType): boolean {
		return this.state.processingLocks.get(dataType) ?? false;
	}

	/**
	 * Get last update timestamp for a data type
	 */
	getLastUpdateTime(dataType: DataType): number | undefined {
		return this.state.lastUpdateTimestamps.get(dataType);
	}

	/**
	 * Get sequence number for a data type
	 */
	getSequenceNumber(dataType: DataType): number {
		return this.state.sequenceNumbers.get(dataType) ?? 0;
	}

	/**
	 * Clear queue for a specific data type
	 */
	clearQueue(dataType?: DataType): void {
		if (dataType) {
			this.state.pendingQueue = this.state.pendingQueue.filter((op) => op.dataType !== dataType);
		} else {
			this.state.pendingQueue = [];
		}

		this.log(`Queue cleared for ${dataType ?? 'all data types'}`);
	}
}

/**
 * Singleton instance
 */
export const unifiedDataUpdateOrchestrator = new UnifiedDataUpdateOrchestrator({
	enableMetrics: true,
	enableDebugLogging: true,
	maxQueueSize: 1000,
	maxRetries: 3,
	retryDelay: 1000,
	processingTimeout: 30000
});
